import React, { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import DeleteModel from '../Common/DeleteModel';
import {toast} from "react-toastify";
import { getAllOrganizationReports, saveOrganizationReport, getOrganizationReportById, deleteOrganizationReportById } from '../../Services/OrganizationService';
import { useAuth } from '../../Context/AuthContext';
import { useParams } from 'react-router-dom';

// Validation schema
const validationSchema = Yup.object({
  title: Yup.string()
    .required('Report title is required')
    .min(2, 'Must be at least 2 characters'),
  reportType: Yup.string().required('Report type is required'),
});

const DEFAULT_REPORTS = [
  {
    title: 'Eligibility & Benefits Verification Report',
    types: [
      '1 day before DOS',
      '2 day before DOS',
      '3 day before DOS',
      '4 day before DOS',
      '5 day before DOS',
    ],
  },
  {
    title: 'Billing Progress report',
    types: ['Daily', 'Weekly', 'Monthly'],
  },
  {
    title: 'Claim Query List',
    types: ['Daily', 'Weekly', 'Monthly'],
  },
  {
    title: 'Unsigned Chart Report',
    types: ['Daily', 'Weekly', 'Monthly'],
  },
  {
    title: 'Denail Management Report',
    types: ['Daily', 'Weekly', 'Monthly'],
  },
  {
    title: 'AR Progress Report',
    types: ['Daily', 'Weekly', 'Monthly'],
  },
];

export default function Reports({ onSaveSuccess, onContinue, organizationId }) {
  const { token } = useAuth();
  const { id: orgIdFromParams } = useParams();
  const effectiveOrgId = organizationId || orgIdFromParams || null;
  const [reports, setReports] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingLoadingId, setEditingLoadingId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [defaultReportSelections, setDefaultReportSelections] = useState(() =>
    DEFAULT_REPORTS.reduce((acc, report) => {
      acc[report.title] = '';
      return acc;
    }, {}),
  );
  const [defaultReportIds, setDefaultReportIds] = useState(() => ({}));
  const [defaultSaveLoading, setDefaultSaveLoading] = useState(null);

  // Handle modal open for adding new report
  const handleOpenModal = () => {
    formik.resetForm();
    setEditingId(null);
    setShowModal(true);
  };

  const handleDefaultReportChange = (title, value) => {
    setDefaultReportSelections((prev) => ({
      ...prev,
      [title]: value,
    }));
  };

  const handleSaveDefaultReport = async (title) => {
    const selectedValue = defaultReportSelections[title] || '';
    if (!selectedValue) {
      toast.error('Please select a report type before saving.');
      return;
    }

    setDefaultSaveLoading(title);
    try {
      const existingId = defaultReportIds[title] || null;
      const payload = {
        id: existingId,
        organization_id: effectiveOrgId || null,
        status_id: 1,
        title,
        value: selectedValue,
        type: existingId ? 'update' : 'create',
      };

      const res = await saveOrganizationReport(payload, token);
      if (res?.is_success) {
        toast.success(res?.message || 'Report saved');
        await loadReports();
      } else if (res?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        console.error('Failed to save report', res);
      } else {
        toast.error(res?.message || 'Failed to save report');
        console.error('Failed to save report', res);
      }
    } catch (error) {
      console.error('Error saving report:', error);
    } finally {
      setDefaultSaveLoading(null);
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    formik.resetForm();
    setEditingId(null);
  };

  // Submit report (add or edit)
  const handleSubmitReport = async (values) => {
    setLoading(true);
    try {
      const payload = {
        id: editingId || null,
        organization_id: values.organizationId || effectiveOrgId || null,
        status_id: 1,
        title: values.title,
        value: values.reportType,
        type: editingId ? 'update' : 'create',
      };

      const res = await saveOrganizationReport(payload, token);
      if (res?.is_success) {
        toast.success(res?.message || 'Report saved');
        handleCloseModal();
        await loadReports();
      } else if(res?.status_code === 500){
        toast.error("Internal Server Error. Please Contact Support Team.");
        console.error('Failed to save report', res);
      }
      else {
        toast.error(res?.message || 'Failed to save report');
        console.error('Failed to save report', res);
      }
    } catch (error) {
      console.error('Error saving report:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReports = async () => {
    setReportsLoading(true);
    try {
      const orgId = effectiveOrgId || organizationId || '';
      if (!orgId) return;
      const res = await getAllOrganizationReports(orgId || '', token);
      if (res?.is_success) {
        const items = Array.isArray(res.data) ? res.data : (Array.isArray(res?.data?.data) ? res.data.data : []);
        const mapped = items.map((r) => {
          const title = r.title ?? r.Title ?? '';
          const value = r.value ?? r.Value ?? '';
          const isCustom = !DEFAULT_REPORTS.some((report) => report.title === title);
          return {
            id: r.id ?? r.Id ?? null,
            organization_id: r.organization_id ?? r.OrganizationId ?? null,
            status_id: r.status_id ?? r.StatusId ?? null,
            status_name: r.status_name ?? r.StatusName ?? null,
            title,
            value,
            reportType: value,
            isCustom,
            created_on: r.created_on ?? r.CreatedOn ?? null,
            created_by: r.created_by ?? r.CreatedBy ?? null,
            modified_on: r.modified_on ?? r.ModifiedOn ?? null,
            modified_by: r.modified_by ?? r.ModifiedBy ?? null,
          };
        });
        setReports(mapped);
        const defaultSelections = { ...defaultReportSelections };
        const defaultIds = {};
        mapped.forEach((report) => {
          if (!report.isCustom && DEFAULT_REPORTS.some((defaultReport) => defaultReport.title === report.title)) {
            defaultSelections[report.title] = report.value || defaultSelections[report.title];
            defaultIds[report.title] = report.id;
          }
        });
        setDefaultReportSelections(defaultSelections);
        setDefaultReportIds(defaultIds);
      } else if(res?.status_code === 500){
        toast.error("Internal Server Error. Please Contact Support Team.");
        console.error('Failed to load reports', res);
        setReports([]);
      }
      else {
        console.error('Failed to load reports', res);
        setReports([]);
      }
    } catch (err) {
      console.error('Error loading reports:', err);
      setReports([]);
    } finally {
      setReportsLoading(false);
    }
  };

  const initialValues = React.useMemo(() => ({
    organizationId: effectiveOrgId,
    title: '',
    reportType: '',
  }), [effectiveOrgId]);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmitReport,
    validateOnChange: true,
    validateOnBlur: true,
  });

  const getFieldError = (fieldName) => {
    return formik.errors[fieldName];
  };

  const getFieldTouched = (fieldName) => {
    return formik.touched[fieldName];
  };

  const renderFieldError = (fieldName) => {
    const error = getFieldError(fieldName);
    const touched = getFieldTouched(fieldName);
    return touched && error ? <small className="text-danger d-block mt-1">{error}</small> : null;
  };

  const customReports = reports.filter((report) => report.isCustom);

  const handleEditReport = (report) => {
    (async () => {
      try {
        setEditingLoadingId(report.id);
        setLoading(true);
        const res = await getOrganizationReportById(report.id, token);
        if (res?.is_success) {
          const d = res.data ?? res;
          formik.setValues({
            title: d.title ?? d.Title ?? report.title,
            reportType: d.value ?? d.Value ?? report.value ?? report.reportType,
          });
          setEditingId(d.id ?? d.Id ?? report.id);
          setShowModal(true);
        } else if(res?.status_code === 500){
          toast.error("Internal Server Error. Please Contact Support Team.");
          console.error('Failed to fetch report by id', res);
          formik.setValues({
            title: report.title,
            reportType: report.value ?? report.reportType,
          });
          setEditingId(report.id);
          setShowModal(true);
        }
        else {
          // fallback to provided report data
          formik.setValues({
            title: report.title,
            reportType: report.value ?? report.reportType,
          });
          setEditingId(report.id);
          setShowModal(true);
        }
      } catch (err) {
        console.error('Error fetching report by id', err);
        formik.setValues({
          title: report.title,
          reportType: report.value ?? report.reportType,
        });
        setEditingId(report.id);
        setShowModal(true);
      } finally {
        setLoading(false);
        setEditingLoadingId(null);
      }
    })();
  };

  const handleDeleteReport = (report) => {
    setReportToDelete(report);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!reportToDelete) return;
    setLoading(true);
    try {
      const res = await deleteOrganizationReportById(reportToDelete.id, token);
      if (res?.is_success) {
        toast.success(res?.message || 'Report deleted');
        setShowDeleteModal(false);
        setReportToDelete(null);
        await loadReports();
      } else if(res?.status_code === 500){
        toast.error("Internal Server Error. Please Contact Support Team.");
        console.error('Failed to delete report', res);
      }
      else {
        console.error('Failed to delete report', res);
      }
    } catch (err) {
      console.error('Error deleting report:', err);
    } finally {
      setLoading(false);
    }
  };

  const renderAsterisk = () => <span className="text-danger">*</span>;

  useEffect(() => {
    loadReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveOrgId, token]);

  return (
    <div className="py-3 px-0 px-md-3">
      <div className="p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-3">
          <h6 className="fw-bold hc-text-primary mb-0">Reports</h6>
          <button
            type="button"
            className="btn hc-bg-primary text-white rounded-3 d-flex align-items-center gap-2 fw-semibold"
            onClick={handleOpenModal}
          >
            <MdAdd size={20} />
            Create Report
          </button>
        </div>

        {reportsLoading ? (
          <div className="text-center py-5">
            <div className="spinner position-absolute d-flex align-items-center justify-content-center">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="row g-3 mb-4">
              <div className="col-12">
                <div className="bg-white rounded-4 border shadow-sm p-4 h-100">
                  <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
                    <div>
                      <h6 className="fw-semibold mb-1">Default Report Card</h6>
                      <p className="text-muted mb-0">Create or update default template reports for this organization.</p>
                    </div>
                  </div>

                  <div className="row g-3 row-cols-1 row-cols-md-2 row-cols-xl-3">
                    {DEFAULT_REPORTS.map((report) => (
                      <div key={report.title} className="col">
                        <div className="bg-light rounded-4 border p-4 h-100 d-flex flex-column justify-content-between">
                          <div>
                            <h6 className="fw-semibold mb-3">{report.title}</h6>
                            <label className="form-label fw-semibold mb-2">Report type</label>
                            <select
                              className="form-select rounded-3"
                              value={defaultReportSelections[report.title] ?? ''}
                              onChange={(e) => handleDefaultReportChange(report.title, e.target.value)}
                            >
                              <option value="">Select Option</option>
                              {report.types.map((typeOption) => (
                                <option key={typeOption} value={typeOption}>{typeOption}</option>
                              ))}
                            </select>
                          </div>
                          <button
                            type="button"
                            className="btn hc-bg-primary text-white fw-semibold rounded-3 mt-4"
                            onClick={() => handleSaveDefaultReport(report.title)}
                            disabled={defaultSaveLoading === report.title || !effectiveOrgId}
                          >
                            {defaultSaveLoading === report.title ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Saving...
                              </>
                            ) : (
                              'Save Template'
                            )}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-12">
                <div className="bg-white rounded-4 border shadow-sm p-4 h-100">
                  <div className="d-flex justify-content-between align-items-start mb-3 gap-2 flex-wrap">
                    <div>
                      <h6 className="fw-semibold mb-1">Custom Report Card</h6>
                      <p className="text-muted mb-0">Manage saved default templates and custom reports for this organization.</p>
                    </div>
                  </div>

                  {customReports.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <p>No custom reports configured yet.</p>
                    </div>
                  ) : (
                    <div className="row g-3 row-cols-1 row-cols-md-2 row-cols-xl-3">
                      {customReports.map((report) => (
                        <div key={report.id} className="col">
                          <div className="bg-light rounded-4 border p-4 h-100 d-flex flex-column justify-content-between">
                            <div className="d-flex justify-content-between align-items-start gap-3 mb-3">
                              <div style={{ minWidth: 0 }}>
                                <h6 className="fw-semibold text-dark mb-2" style={{ wordBreak: 'break-word' }}>
                                  {report.title}
                                </h6>
                                <span className="badge bg-secondary text-white">
                                  {report.status_name || report.value || 'No status'}
                                </span>
                              </div>
                              <div className="d-flex gap-2 flex-shrink-0">
                                <button
                                  type="button"
                                  className="btn btn-sm p-2 lh-1 hc-text-primary border-0"
                                  style={{ backgroundColor: 'var(--hc-icon-blue-bg)', borderRadius: '10px' }}
                                  title="Edit"
                                  onClick={() => handleEditReport(report)}
                                  disabled={editingLoadingId === report.id}
                                >
                                  {editingLoadingId === report.id ? (
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true" />
                                  ) : (
                                    <MdEdit size={16} />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm p-2 lh-1 text-danger border-0"
                                  style={{ backgroundColor: 'var(--hc-status-danger-bg)', borderRadius: '10px' }}
                                  title="Delete"
                                  onClick={() => handleDeleteReport(report)}
                                >
                                  <MdDelete size={16} />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Report Modal */}
      {showModal && (
        <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(var(--hc-black-rgb), 0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header border-bottom">
                <h5 className="modal-title fw-bold">
                  {editingId ? 'Edit Report' : 'Add Report'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={handleCloseModal}
                  aria-label="Close"
                />
              </div>
              <form onSubmit={formik.handleSubmit} noValidate>
                <div className="modal-body">
                  <div className="mb-3">
                    <label className="form-label fw-semibold">
                      Title {renderAsterisk()}
                    </label>
                    <input
                      type="text"
                      className={`form-control rounded-3 shadow-sm ${
                        getFieldTouched('title') && getFieldError('title') ? 'is-invalid' : ''
                      }`}
                      placeholder="e.g. Monthly Performance Report"
                      {...formik.getFieldProps('title')}
                    />
                    {renderFieldError('title')}
                  </div>

                  <div className="mb-0">
                    <label className="form-label fw-semibold">
                      Report Type {renderAsterisk()}
                    </label>
                    <select
                      className={`form-control rounded-3 shadow-sm ${
                        getFieldTouched('reportType') && getFieldError('reportType') ? 'is-invalid' : ''
                      }`}
                      {...formik.getFieldProps('reportType')}
                    >
                      <option value="">Select Report Type</option>
                      <option value="Daily">Daily</option>
                      <option value="Weekly">Weekly</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Yearly">Yearly</option>
                    </select>
                    {renderFieldError('reportType')}
                  </div>
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary rounded-3"
                    onClick={handleCloseModal}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn hc-bg-primary text-white rounded-3"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                        Saving...
                      </>
                    ) : editingId ? 'Update Report' : 'Add Report'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <DeleteModel
        isOpen={showDeleteModal}
        title="Delete Report"
        description="This will permanently delete this report. This action cannot be undone."
        entityLabel="report"
        itemName={reportToDelete?.title}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onClose={() => {
          setShowDeleteModal(false);
          setReportToDelete(null);
        }}
        isLoading={loading}
      />

      {/* Footer with Save & Continue Button */}
      <div className="d-flex justify-content-end gap-2 mt-4">
        <button
          type="button"
          className="btn hc-bg-primary text-white px-4"
          disabled={loading}
          onClick={onContinue}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Saving...
            </>
          ) : (
            'Finish'
          )}
        </button>
      </div>
    </div>
  );
}
